import "./styles/_index.scss";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import OperationsComponent from "../operations/operations";
import { useEffect, useState, useRef, useMemo } from "react";
import TextField from "@mui/material/TextField";
import {
  Button,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import axios from "axios";

const isRefresh = sessionStorage.getItem("isRefresh");
if (!isRefresh) {
  const lat = localStorage.getItem("lat");
  const lon = localStorage.getItem("lon");

  localStorage.clear();
  sessionStorage.clear();

  if (lat) localStorage.setItem("lat", lat);
  if (lon) localStorage.setItem("lon", lon);
}
sessionStorage.setItem("isRefresh", "true");

export default function AuthPage() {
  const [error, setError] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [step, setStep] = useState<number | string | null>(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const params = new URLSearchParams(hash.substring(1));
      if (params.get("access_token")) return null;
    }
    return sessionStorage.getItem("loginStep") ?? 1;
  });
  const [password, setPassword] = useState<string>("");
  const [isAdmin, setIsAdmin] = useState<boolean>(
    localStorage.getItem("type") === "admin" ? true : false,
  );
  const [showOperations, setShowOperations] = useState(true);
  const [ssoClientIds, setSSOClientIds] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem("sso_client_ids");
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [isSSOMultiClient, setIsSSOMultiClient] = useState<boolean>(
    localStorage.getItem("sso_multi_client") === "true"
  );
  const [ssoLoading, setSsoLoading] = useState<boolean>(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const params = new URLSearchParams(hash.substring(1));
      return !!params.get("access_token");
    }
    return false;
  });

  const ssoProcessedRef = useRef(false);
  const ssoClientFetchRef = useRef<Promise<{ clientIds: string[]; clientNames: string[] } | null> | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  async function proceedLogin(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (!name.trim()) {
      setError(true);
      setErrorMsg("Please enter your username.");
      return;
    }

    if (!password.trim()) {
      setError(true);
      setErrorMsg("Please enter your password.");
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_IP}/api/login/`, {
        method: "POST",
        body: JSON.stringify({ name, password }),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          Authorization: "Basic " + btoa("admin:admin"),
        },
      });

      if (res.status === 200) {
        const data = await res.json();
        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("login", "true");
        sessionStorage.setItem("token", data.token);
        localStorage.setItem("user_name", data.user);
        localStorage.setItem("type", data.user_type);
        localStorage.setItem("client_id", data.client_id);
        localStorage.setItem("fid", data.forecast_id);
        localStorage.setItem("sideNav", "true");
        localStorage.setItem("lat", data.lat);
        localStorage.setItem("lon", data.lon);
        localStorage.removeItem("sso_multi_client");
        localStorage.removeItem("sso_client_ids");
        localStorage.removeItem("sso_client_names");
        localStorage.removeItem("sso_prefixes");
        localStorage.removeItem("fid");
        localStorage.removeItem("operation");
        localStorage.removeItem("project");
        sessionStorage.removeItem("auth_operation");

        setIsAdmin(data.user_type === "admin");
        setStep(2);
        sessionStorage.setItem("loginStep", "2");
      } else {
        const data = await res.json();
        setError(true);
        setErrorMsg(data.data);
      }
    } catch (error) {
      setError(true);
      if (
        error instanceof TypeError &&
        (error.message.includes("fetch") ||
          error.message.includes("Failed to fetch") ||
          error.message.includes("NetworkError") ||
          error.message.includes("ERR_INTERNET_DISCONNECTED") ||
          error.message.includes("ERR_NETWORK_CHANGED"))
      ) {
        setErrorMsg("Network issue. Please check your internet connection and try again.");
      } else if (error instanceof Error && error.name === "AbortError") {
        setErrorMsg("Request was cancelled. Please try again.");
      } else if (
        error instanceof Error &&
        (error.message.includes("timeout") || error.message.includes("TIMEOUT"))
      ) {
        setErrorMsg("Request timed out. Please check your connection and try again.");
      } else if (
        error instanceof Error &&
        (error.message.includes("CORS") || error.message.includes("Cross-Origin"))
      ) {
        setErrorMsg("Security restriction. Please contact support.");
      } else {
        setErrorMsg("Something went wrong. Please try again.");
      }
    }
  }

    function handleSSOLogin(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    const azureSSOURL = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
    const clientId = "971024d6-003d-4e7c-8a04-364b7fe3cecb";
    // const redirectURI = encodeURIComponent("http://localhost:3001/auth");
    const redirectURI = encodeURIComponent("https://weather.fugro.com/auth");
    const ssoLoginURL = `${azureSSOURL}?client_id=${clientId}&response_type=token&redirect_uri=${redirectURI}&scope=openid profile email`;
    window.location.href = ssoLoginURL;
  }

  const fetchSSOClientIds = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_IP}/api/getSSOClients/`,
        { headers: { "Content-Type": "application/json" } },
      );

      const { clients: matchedClients, prefixes: allPrefixes } = response.data;

      if (!matchedClients || matchedClients.length === 0) {
        setError(true);
        setErrorMsg("SSO clients not found. Please contact support.");
        return null;
      }

      const clientIds = matchedClients.map((client: any) => client.client_id.toString());
      const clientNames = matchedClients.map((client: any) => client.client_name.trim());

      localStorage.setItem("sso_prefixes", JSON.stringify(allPrefixes));

      return { clientIds, clientNames };
    } catch (error) {
      setError(true);
      setErrorMsg("Failed to load SSO client configuration.");
      return null;
    }
  };

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1) {
      const params = new URLSearchParams(hash.substring(1));
      if (params.get("access_token")) {
        ssoClientFetchRef.current = fetchSSOClientIds();
      }
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem("login") !== "true") {
      sessionStorage.removeItem("loginStep");
      setStep(1);
    }
  }, []);

  useEffect(() => {
    if (ssoProcessedRef.current) return;

    const hash = window.location.hash;
    if (!hash || hash.length <= 1) return;

    ssoProcessedRef.current = true;

    const processSSO = async () => {
      try {
        const params = new URLSearchParams(hash.substring(1));
        const accessToken = params.get("access_token");
        const ssoError = params.get("error");

        if (ssoError) {
          const errorDescription = params.get("error_description");
          setError(true);
          setErrorMsg(errorDescription || "SSO authentication failed. Please try regular login.");
          window.history.replaceState(null, "", window.location.pathname);
          ssoProcessedRef.current = false;
          setSsoLoading(false);
          return;
        }

        if (accessToken && accessToken.length > 10) {
          window.history.replaceState(null, "", window.location.pathname);
          sessionStorage.setItem("token", accessToken);

          const clientData = ssoClientFetchRef.current
            ? await ssoClientFetchRef.current
            : await fetchSSOClientIds();

          if (!clientData) {
            setError(true);
            setErrorMsg("Failed to load SSO configuration.");
            ssoProcessedRef.current = false;
            setSsoLoading(false);
            return;
          }

          const { clientIds, clientNames } = clientData;

          localStorage.setItem("sso_multi_client", "true");
          localStorage.setItem("sso_client_ids", JSON.stringify(clientIds));
          localStorage.setItem("sso_client_names", JSON.stringify(clientNames));
          localStorage.setItem("login", "true");
          localStorage.setItem("sideNav", "true");
          localStorage.setItem("type", "user");
          localStorage.setItem("client_id", clientIds[0]);
          localStorage.setItem("user_name", `SSO-${clientNames[0]}`);
          localStorage.removeItem("fid");
          localStorage.removeItem("operation");
          localStorage.removeItem("project");
          sessionStorage.removeItem("auth_operation");

          setSSOClientIds(clientIds);
          setIsSSOMultiClient(true);
          setIsAdmin(false);
          setStep(2);
          sessionStorage.setItem("loginStep", "2");
          setSsoLoading(false);
        }
      } catch (error) {
        setError(true);
        setErrorMsg("Error processing SSO login. Please try regular login.");
        window.history.replaceState(null, "", window.location.pathname);
        ssoProcessedRef.current = false;
        setSsoLoading(false);
      }
    };

    processSSO();
  }, []);

  useEffect(() => {
    if (error) {
      const timeout = setTimeout(() => setError(false), 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const closeOperations = () => {
    setShowOperations(false);
    navigate("/dashboard");
  };

  const operationsKey = useMemo(
    () => `ops-${isAdmin}-${isSSOMultiClient}-${ssoClientIds.join(",")}`,
    [isAdmin, isSSOMultiClient, ssoClientIds],
  );

  if (ssoLoading) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        gap: 16,
        backgroundColor: "#ffffff",
      }}>
        <CircularProgress size={48} style={{ color: "#011e41" }} />
        <Typography style={{ color: "#011e41", fontSize: 18 }}>Loading…</Typography>
      </div>
    );
  }

  return (
    <div>
      {step == 1 ? (
        <div className={"login-centre-div"}>
          <div className={"login-box"}>
            <Grid container className="login-container">
              <Grid
                item
                xs={12}
                sm={6}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <form className="login-form">
                  {error && (
                    <Alert
                      style={{
                        width:
                          window.innerWidth <= 320 ? "70%" :
                            window.innerWidth <= 480 ? "60%" :
                              window.innerWidth <= 768 ? "50%" :
                                window.innerWidth <= 1024 ? "40%" : "25%",
                        marginTop: isMobile ? "-240px" : "90px",
                        alignItems: "center",
                        position: "absolute",
                        zIndex: 10,
                        padding:
                          window.innerWidth <= 480 ? "3px 6px" :
                            window.innerWidth <= 768 ? "4px 8px" : "6px 16px",
                        minHeight:
                          window.innerWidth <= 480 ? "24px" :
                            window.innerWidth <= 768 ? "28px" : "48px",
                      }}
                      severity="error"
                      sx={{
                        "& .MuiAlert-message": {
                          fontSize:
                            window.innerWidth <= 320 ? "9px" :
                              window.innerWidth <= 480 ? "10px" :
                                window.innerWidth <= 768 ? "11px" : "14px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                        "& .MuiAlert-icon": {
                          fontSize:
                            window.innerWidth <= 480 ? "12px" :
                              window.innerWidth <= 768 ? "14px" : "20px",
                        },
                      }}
                    >
                      {errorMsg}
                    </Alert>
                  )}
                  <div
                    className="login-framesize"
                    style={{
                      backgroundColor: "rgba(1, 30, 65, 0.9)",
                      padding: 30,
                      paddingTop: 50,
                      marginTop: 90,
                      borderRadius: 10,
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: 10,
                      width: isMobile ? "90%" : "50%",
                    }}
                  >
                    <TextField
                      label="User Name"
                      variant="filled"
                      className={"login-input-text"}
                      name="username"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      type="text"
                      placeholder="Enter the Username"
                      required
                    />
                    <TextField
                      label="Password"
                      variant="filled"
                      className={"login-input-text"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      name="password"
                      type="password"
                      placeholder="Enter the Password"
                      required
                    />
                    <div style={{ display: "flex", flexDirection: "row" }}>
                      <Button
                        onClick={proceedLogin}
                        style={{ width: 150 }}
                        className={"login-submit"}
                        type="submit"
                        variant="contained"
                      >
                        Login
                      </Button>
                    </div>
                    {/* <Typography variant="body2" className={"login-signup"} style={{ color: "white" }}>
                      Forgotten Password?
                    </Typography> */}
                    <Typography variant="body2" style={{ textAlign: "center", margin: "10px 0", color: "white" }}>
                      -------- or --------
                    </Typography>
                    <Typography variant="body2" style={{ textAlign: "center", fontWeight: "bold" }}>
                      <Button
                        onClick={handleSSOLogin}
                        style={{ width: 200 }}
                        className={"login-submit"}
                        type="button"
                        variant="contained"
                      >
                        Sign in with SSO
                      </Button>
                    </Typography>
                  </div>
                </form>
              </Grid>
              <Grid item xs={12} sm={6} className="login-image-container"></Grid>
            </Grid>
          </div>
        </div>
      ) : step == 2 ? (
        <OperationsComponent
          key={operationsKey}
          isAdmin={isAdmin}
          open={showOperations}
          close={closeOperations}
          ssoMultiClient={isSSOMultiClient}
          ssoClientIds={ssoClientIds}
        />
      ) : null}
    </div>
  );
}